import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useImportHistoryListQuery, useImportProgressSubscription } from '@/common/graphql';
import { ApiUtils } from '@/common/utils';
import {
  type ImportHistoryRowData,
  toImportHistoryRow,
  toImportProgressItem,
  type UseImportHistoryResult,
} from './types';

type LiveProgress = Pick<ImportHistoryRowData, 'status' | 'updatedAt' | 'totalRequestCount' | 'completedRequestCount'>;

/**
 * Single hook backing the History tab: runs the one unified `listImportHistories` query and the one
 * unified `importProgress` subscription (both kinds merged/sorted server-side — see
 * `EmployeeExportImportService.listImportHistories`), then merges live progress into the query
 * snapshot the same way the per-kind hooks did before Increment 2 (a `liveProgressById` map updated
 * on each subscription tick, refetching once a row disappears from the incomplete-only payload to
 * pick up its terminal status/errors).
 */
export const useImportHistory = (onlyMyTask: boolean): UseImportHistoryResult => {
  const { data, loading, error, refetch } = useImportHistoryListQuery({
    fetchPolicy: 'cache-and-network',
    variables: { onlyMyTask },
  });
  const history = useMemo(() => (data?.importHistoryList ?? []).map(toImportHistoryRow), [data?.importHistoryList]);

  const { data: progressData } = useImportProgressSubscription();
  const progressList = useMemo(
    () => (progressData?.importProgress ?? []).map(toImportProgressItem),
    [progressData?.importProgress],
  );

  const [liveProgressById, setLiveProgressById] = useState<Record<string, LiveProgress>>({});
  const previousIncompleteIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (error) ApiUtils.handleApiError(error);
  }, [error]);

  useEffect(() => {
    // A row present in the previous emission but absent from this one has settled (reached
    // DONE/FAILED) — it no longer appears in the incomplete-only subscription payload.
    const currentIds = new Set(progressList.map(item => item.id));
    const settledIds = [...previousIncompleteIdsRef.current].filter(id => !currentIds.has(id));
    previousIncompleteIdsRef.current = currentIds;

    setLiveProgressById(prev => {
      const next = { ...prev };
      for (const item of progressList) {
        next[item.id] = {
          status: item.status,
          updatedAt: item.updatedAt,
          totalRequestCount: item.totalRequestCount,
          completedRequestCount: item.completedRequestCount,
        };
      }
      // Drop settled rows' stale IN_PROGRESS snapshot — otherwise it permanently overrides the
      // refetched row's real terminal status in the `rows` merge below, since this map is never
      // cleared once set.
      for (const id of settledIds) {
        delete next[id];
      }
      return next;
    });

    // Refetch the full history query to pick up the settled row's terminal status/errors, which the
    // lightweight Progress payload never carries.
    if (settledIds.length > 0) {
      refetch();
    }
  }, [progressList, refetch]);

  const rows: ImportHistoryRowData[] = useMemo(
    () =>
      history
        .map(row => ({ ...row, ...liveProgressById[row.id] }))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [history, liveProgressById],
  );

  // Stable across renders (as long as Apollo's own `refetch` is) — the settle-detection effect
  // above depends on this reference; a fresh function every render would re-fire that effect every
  // render regardless of whether `progressList` actually changed.
  const stableRefetch = useCallback(() => void refetch(), [refetch]);

  return { error, loading, refetch: stableRefetch, rows };
};
